import React from 'react';
import { AppShell } from '@/components';
import { MarketingSection, SectionHeader, GlassPanel } from '@/components/marketing';

const resourceCards = [
  {
    type: 'Guide',
    title: 'Relay quickstart for platform teams',
    description: 'Stand up namespaces, policies, and telemetry in under an hour.',
    href: '/docs/guides/platform-quickstart',
  },
  {
    type: 'Webinar',
    title: 'Unlock multi-cloud without losing control',
    description: 'Relay founders walk through architecture patterns and guardrails.',
    href: '/resources/webinars/multi-cloud-control',
  },
  {
    type: 'ROI brief',
    title: 'From internal functions to revenue products',
    description: 'Finance-ready model for monetizing reusable logic on Relay.',
    href: '/resources/roi/relay-control-plane',
  },
  {
    type: 'Runbook',
    title: 'Execution incident response playbook',
    description: 'Step-by-step workflows for investigating and resolving execution incidents.',
    href: '/docs/runbooks/execution-incidents',
  },
  {
    type: 'Case study',
    title: 'How AlloyEdge scaled global checkout on Relay',
    description: 'Composite functions, policy automation, and monetization in practice.',
    href: '/resources/customers/alloyedge',
  },
  {
    type: 'Toolkit',
    title: 'Compliance starter kit',
    description: 'Templates for policy approvals, evidence collection, and tenant audits.',
    href: '/resources/toolkits/compliance-starter',
  },
];

const filters = ['All', 'Guide', 'Webinar', 'ROI', 'Runbook', 'Case study', 'Toolkit'];

const ResourcesPage: React.FC = () => {
  return (
    <AppShell>
      <div className="bg-slate-950 text-slate-50">
        <MarketingSection theme="gradient" collapsePadding>
          <div className="grid gap-12 py-16 lg:grid-cols-[1.2fr_0.8fr] lg:py-24">
            <div>
              <SectionHeader
                overline="Resource library"
                title="Everything you need to run, monetize, and govern serverless programs on Relay"
                description="Browse guides, webinars, toolkits, and ROI briefs curated for engineers, operators, and revenue teams. New content drops every sprint as the platform evolves."
              />
              <div className="mt-8 flex flex-wrap gap-3 text-sm text-slate-300">
                <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1">Architecture</span>
                <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1">Operations</span>
                <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1">Monetization</span>
              </div>
            </div>
            <GlassPanel padding="lg" className="space-y-4">
              <label htmlFor="resource-search" className="text-sm font-medium text-slate-200">
                Search resources
              </label>
              <input
                id="resource-search"
                type="search"
                placeholder="Search by topic, role, or keyword"
                className="w-full rounded-xl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-emerald-400 focus:outline-none"
              />
              <p className="text-xs text-slate-400">Tip: Filter by persona or outcome to find exactly what your team needs.</p>
            </GlassPanel>
          </div>
        </MarketingSection>

        <MarketingSection theme="dark">
          <div className="flex flex-wrap items-center gap-3">
            {filters.map((filter) => (
              <button
                key={filter}
                className={`rounded-full border px-4 py-2 text-sm transition ${
                  filter === 'All'
                    ? 'border-emerald-500 bg-emerald-500/10 text-white'
                    : 'border-white/15 bg-white/5 text-slate-300 hover:border-white/40 hover:bg-white/10'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-2 xl:grid-cols-3">
            {resourceCards.map((card) => (
              <GlassPanel
                key={card.title}
                padding="lg"
                className="flex h-full flex-col justify-between transition hover:border-emerald-400 hover:bg-emerald-500/10"
              >
                <div className="space-y-3">
                  <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-300">
                    {card.type}
                  </span>
                  <h3 className="text-lg font-semibold text-white">{card.title}</h3>
                  <p className="text-sm text-slate-300">{card.description}</p>
                </div>
                <a
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-emerald-300"
                  href={card.href}
                >
                  View resource →
                </a>
              </GlassPanel>
            ))}
          </div>
        </MarketingSection>

        <MarketingSection theme="deep" collapsePadding>
          <GlassPanel padding="xl" className="mx-auto max-w-5xl text-center space-y-8">
            <SectionHeader
              overline="Newsletter"
              title="Subscribe for updates"
              description="Get new guides, webinars, and product updates delivered to your inbox. No spam—just practical control-plane insights."
              align="center"
            />
            <form className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <label htmlFor="resource-email" className="sr-only">
                Email address
              </label>
              <input
                id="resource-email"
                type="email"
                required
                placeholder="you@company.com"
                className="w-full max-w-sm rounded-xl border border-white/10 bg-slate-950 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-emerald-400 focus:outline-none"
              />
              <button
                type="submit"
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-6 py-3 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400"
              >
                Subscribe
              </button>
            </form>
          </GlassPanel>
        </MarketingSection>
      </div>
    </AppShell>
  );
};

export default ResourcesPage;
